import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { defineComponent, h, onMounted, ref, resolveComponent } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import {
  CBadge,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CNavTitle,
} from '@coreui/vue-pro'
import nav from '@/_nav.js'

const normalizePath = (path) =>
  decodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route, link) => {
  if (link === undefined) {
    return false
  }

  if (route.hash === link) {
    return true
  }

  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)

  return currentPath === targetPath
}

const isActiveItem = (route, item) => {
  if (isActiveLink(route, item.to)) {
    return true
  }

  if (item.items) {
    return item.items.some((child) => isActiveItem(route, child))
  }

  return false
}

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
    FontAwesomeIcon,
  },
  setup() {
    const route = useRoute()
    const store = useStore()
    const firstRender = ref(true)

    onMounted(() => {
      firstRender.value = false
    })

    const hasPermission = (requiredPermissions) => {
      // If permissions haven't loaded yet, be permissive for empty requirements
      if (store.state.permissions === null) {
        return requiredPermissions.length === 0
      }
      
      // Use the store's hierarchical permission checking
      return requiredPermissions.every((permission) =>
        store.getters.hasPermission(permission)
      )
    }

    const filterItems = (items) => {
      return items.filter((item) => {
        if (
          item.meta?.requiredPermissions &&
          item.meta.requiredPermissions.length > 0
        ) {
          if (!hasPermission(item.meta.requiredPermissions)) {
            return false
          }
        }
        if (item.items) {
          item.items = filterItems(item.items)
          return item.items.length > 0
        }
        return true
      })
    }

    const renderItem = (item) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child)),
            }),
          },
          {
            togglerContent: () => [
              item.faIcon &&
                h(FontAwesomeIcon, {
                  icon: item.faIcon,
                  class: 'nav-icon',
                }),
              item.name,
            ],
            default: () => item.items.map((child) => renderItem(child)),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              default: (props) =>
                h(
                  resolveComponent(item.component),
                  {
                    active: props.isActive,
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  {
                    // <font-awesome-icon icon="fab fa-paypal"></font-awesome-icon>
                    default: () => [
                      item.faIcon &&
                        h(FontAwesomeIcon, {
                          icon: item.faIcon,
                          class: 'nav-icon',
                        }),
                      item.name,
                      item.badge &&
                        h(
                          CBadge,
                          {
                            class: 'ms-auto',
                            color: item.badge.color,
                          },
                          {
                            default: () => item.badge.text,
                          },
                        ),
                    ],
                  },
                ),
            },
          )
        : h(
            resolveComponent(item.component),
            {},
            {
              default: () => item.name,
            },
          )
    }

    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => filterItems(nav).map((item) => renderItem(item)),
        },
      )
  },
})
export { AppSidebarNav }
